/**
 * Reusable Progress Bar Component
 * Shows usage progress with customizable colors and labels
 */

interface ProgressBarProps {
  current: number;
  max: number;
  label?: string;
  showValues?: boolean;
  className?: string;
  barColor?: string;
  height?: 'sm' | 'md' | 'lg';
}

export default function ProgressBar({
  current,
  max,
  label,
  showValues = true,
  className = '',
  barColor = 'bg-accent-blue',
  height = 'md'
}: ProgressBarProps) {
  const percentage = Math.min((current / max) * 100, 100);
  const isNearLimit = percentage >= 80;
  const isAtLimit = percentage >= 100;

  const heightClasses = {
    sm: 'h-1.5',
    md: 'h-2',
    lg: 'h-3'
  };

  // Determine bar color based on usage
  const getBarColor = () => {
    if (isAtLimit) return 'bg-red-500';
    if (isNearLimit) return 'bg-yellow-500';
    return barColor;
  };

  return (
    <div className={`space-y-2 ${className}`}>
      {(label || showValues) && (
        <div className="flex justify-between items-center">
          {label && (
            <span className="text-sm text-dark-text-muted">{label}</span>
          )}
          {showValues && (
            <span className={`text-sm font-semibold ${isAtLimit ? 'text-red-500' : isNearLimit ? 'text-yellow-500' : 'text-dark-text'}`}>
              {current} / {max}
            </span>
          )}
        </div>
      )}
      <div className={`w-full bg-dark-border/30 rounded-full ${heightClasses[height]}`}>
        <div
          className={`${getBarColor()} rounded-full ${heightClasses[height]} transition-all duration-300`}
          style={{ width: `${percentage}%` }}
          aria-valuenow={current}
          aria-valuemin={0}
          aria-valuemax={max}
          role="progressbar"
        />
      </div>
    </div>
  );
}
